import { useState } from "react";
import { createFileRoute, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { HrefLink as Link } from "@/components/ui/href-link";

import { AuthField, CONTROL } from "@/components/dashboard/auth-field";
import { SkeletonPanel } from "@/components/dashboard/skeletons";
import { buttonVariants } from "@/components/ui/button";
import { app, auth } from "@/content";
import { inspectResubmit, submitResubmitApplication } from "@/hooks/api/auth";
import { cn } from "@/utils";

/**
 * Rejected applicant corrects name / email / phone and returns to Pending.
 * Prefills from the resubmit token issued at rejection.
 */
export const Route = createFileRoute("/_auth/_anonymous/resubmit")({
  component: ResubmitPage,
});

function ResubmitPage() {
  const searchStr = useRouterState({ select: (s) => s.location.searchStr });
  const token =
    new URLSearchParams(searchStr.startsWith("?") ? searchStr.slice(1) : searchStr).get("token") ??
    "";

  const { data: check, isPending } = useQuery({
    queryKey: ["auth", "resubmit", token],
    queryFn: () => inspectResubmit(token),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: token.length > 0,
  });

  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return <ResubmitRefused lead={auth.resubmitExpiredLead} />;
  }

  if (isPending || !check) {
    return (
      <div className="rounded-xl border border-white/10 bg-navy-800 p-7 shadow-navy">
        <SkeletonPanel height="12rem" />
      </div>
    );
  }

  if (!check.ok) {
    return (
      <ResubmitRefused
        lead={check.reason === "used" ? auth.resubmitUsedLead : auth.resubmitExpiredLead}
      />
    );
  }

  if (done) {
    return (
      <div className="flex flex-col gap-5 rounded-xl border border-white/10 bg-navy-800 p-7 shadow-navy">
        <div>
          <span className="bracket">{app.name}</span>
          <h1 className="mt-5 text-lg font-bold leading-head text-on-navy">{auth.resubmitDoneTitle}</h1>
          <p className="mt-2 text-sm leading-body text-on-navy-dim">{auth.resubmitDoneLead}</p>
        </div>
        <Link to="/login" className={buttonVariants({ variant: "brand", size: "lg" })}>
          {auth.backToSignIn}
        </Link>
      </div>
    );
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setFieldErrors({});

    const form = new FormData(event.currentTarget);
    form.set("token", token);
    const result = await submitResubmitApplication(form);
    if (!result.ok) {
      setError(result.error);
      if (result.fieldErrors) setFieldErrors(result.fieldErrors);
      setSaving(false);
      return;
    }
    setDone(true);
    setSaving(false);
  }

  return (
    <form
      onSubmit={onSubmit}
      className="flex flex-col gap-5 rounded-xl border border-white/10 bg-navy-800 p-7 shadow-navy"
      noValidate
    >
      <div>
        <span className="bracket">{app.name}</span>
        <h1 className="mt-5 text-lg font-bold leading-head text-on-navy">{auth.resubmitTitle}</h1>
        <p className="mt-2 text-sm leading-body text-on-navy-dim">{auth.resubmitLead}</p>
        {check.rejectionReason ? (
          <p className="mt-3 rounded-lg border border-white/10 bg-navy-900/60 p-3 text-sm text-on-navy-dim">
            <span className="font-semibold text-on-navy">{auth.resubmitReasonLabel}</span>
            <span className="mt-1 block leading-body">{check.rejectionReason}</span>
          </p>
        ) : null}
      </div>

      {error ? (
        <p role="alert" className="text-sm font-semibold text-red-300">
          {error}
        </p>
      ) : null}

      <AuthField id="name" label={auth.resubmitName} required error={fieldErrors.name}>
        <input
          id="name"
          name="name"
          className={CONTROL}
          defaultValue={check.name}
          autoComplete="name"
          required
          aria-invalid={fieldErrors.name ? true : undefined}
        />
      </AuthField>

      <AuthField id="email" label={auth.emailLabel} required error={fieldErrors.email}>
        <input
          id="email"
          name="email"
          type="email"
          className={CONTROL}
          defaultValue={check.email}
          autoComplete="email"
          dir="ltr"
          required
          aria-invalid={fieldErrors.email ? true : undefined}
        />
      </AuthField>

      <AuthField id="phone" label={auth.resubmitPhone} error={fieldErrors.phone}>
        <input
          id="phone"
          name="phone"
          className={CONTROL}
          defaultValue={check.phone ?? ""}
          autoComplete="tel"
          dir="ltr"
          aria-invalid={fieldErrors.phone ? true : undefined}
        />
      </AuthField>

      <button
        type="submit"
        disabled={saving}
        className={cn(buttonVariants({ variant: "brand", size: "lg" }), "w-full")}
      >
        {saving ? auth.saving : auth.resubmitSubmit}
      </button>
    </form>
  );
}

function ResubmitRefused({ lead }: { lead: string }) {
  return (
    <div className="flex flex-col gap-5 rounded-xl border border-white/10 bg-navy-800 p-7 shadow-navy">
      <div>
        <span className="bracket">{app.name}</span>
        <h1 className="mt-5 text-lg font-bold leading-head text-on-navy">{auth.linkInvalidTitle}</h1>
        <p className="mt-2 text-sm leading-body text-on-navy-dim">{lead}</p>
      </div>
      <Link to="/login" className={cn(buttonVariants({ variant: "brand", size: "lg" }), CONTROL)}>
        {auth.backToSignIn}
      </Link>
    </div>
  );
}
